import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Select,
  MenuItem,
  FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TopSection from "../LT/TopSection";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "../../components/common/Loader";
import "./VideoPlayer.css";
import { getAppAdminTrainingTopicSlice } from "../../redux/actions/getAppAdminTrainingTopicSlice";
import { getAppAdminTrainingContentEndpoint } from "../../common/endpoints";

const topicData = {
  "User Onboarding Overview": {
    video: "/videos/animal.mp4",
    pdfs: [
      {
        name: "User Onboarding Checklist",
        file: "/pdfs/sample-1.pdf",
      },
    ],
  },
  "Navigate Forecast": {
    video: "/videos/animal.mp4",
    pdfs: [
      {
        name: "Forecast Guide",
        file: "/pdfs/sample-1.pdf",
      },
    ],
  },
  "Creating Support Ticket": {
    video: "/videos/animal.mp4",
    pdfs: [
      {
        name: "Support Ticket Guide",
        file: "/pdfs/sample-1.pdf",
      },
    ],
  },
};


const TrainingContent = () => {
  const videoRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [openPdf, setOpenPdf] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const dispatch = useDispatch();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [buffering, setBuffering] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const { loading } = useSelector((state) => state.getAppAdminTrainingTopicSlice);

  useEffect(() => {
    if (openModal && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => { });
      }
    }
  }, [openModal]);

  const handleTopicChange = (event) => {
    setSelectedTopic(event.target.value);
  };
  const selectedTopicData = topics.find(
    (t) => t.topic === selectedTopic
  );
  const videoUrl = selectedTopicData?.videoUrl;
  const pdfList = selectedTopicData
    ? [
      {
        name: selectedTopicData.pdfName,
        file: selectedTopicData.pdfUrl,
      },
    ]
    : [];

  useEffect(() => {
    getAllTrainingTopics();
  }, []);

  const getAllTrainingTopics = async () => {
    try {
      const response = await dispatch(
        getAppAdminTrainingTopicSlice({
          dynamicEndpoint: getAppAdminTrainingContentEndpoint
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        const list = response?.payload || [];

        const formatted = list.map((item) => ({
          id: item.topicId,
          topic: item.topicName,
          description: item.description,

          videoTitle: item.videoTitle,
          videoUrl: item.azureBlobUrl,

          pdfName: item.jobAidLabel,
          pdfUrl: item.jobAidUrl,
        }));

        setTopics(formatted);

        // auto select first topic
        if (formatted.length > 0) {
          setSelectedTopic(formatted[0].topic);
        }
      }
    } catch (error) {
      console.error("GET TOPICS ERROR", error);
    }
  };

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);

    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [selectedTopic]);

  const togglePlay = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (videoUrl) {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.preload = "auto";
    }
  }, [videoUrl]);

  const forward = () => {
    videoRef.current.currentTime += 10;
  };

  const backward = () => {
    videoRef.current.currentTime -= 10;
  };

  const handleTimeUpdate = () => {
    setProgress(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    videoRef.current.currentTime = e.target.value;
    setProgress(e.target.value);
  };

  const handleVolume = (e) => {
    videoRef.current.volume = e.target.value;
    setVolume(e.target.value);
  };

  const fullScreen = () => {
    const el = videoRef.current.parentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleOpenPdf = (pdf) => {
    setSelectedPdf(pdf);
    setOpenPdf(true);
  };

  const handleClosePdf = () => {
    setOpenPdf(false);
  };

  const downloadVideo = async () => {
    if (!videoUrl) return;

    try {
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error("Failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = selectedTopicData?.videoTitle;

      document.body.appendChild(link); // IMPORTANT
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log("Fallback open");
      window.open(videoUrl, "_blank");
    }
  };

  const handlePdfDownload = async () => {
    if (!selectedPdf?.file) return;

    try {
      const response = await fetch(selectedPdf.file);
      if (!response.ok) throw new Error("Fetch failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = selectedPdf?.name;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("CORS blocked → fallback");

      // fallback MUST use new tab
      window.open(selectedPdf.file, "_blank");
    }
  };

  const isEmpty = topics.length === 0;
  const handleOpenVideo = () => {
    setLoadVideo(true);
    setOpenModal(true);
  };

  const isVideoReady = isFinite(duration) && duration > 0;

  return (
    <Box>
      <TopSection
        labelList={[
          { title: "Home", path: "/dashboard" },
          { title: "Training Content" },
        ]}
        headerName="Training Content"
        hideHeader
      />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Loader />
        </Box>
      ) : isEmpty ? (
        <Typography color="textSecondary" paddingBottom={5} gap={2}>
          No training content available
        </Typography>) : (
        <>

          <Box display="flex" alignItems="center" paddingBottom={5} gap={2}>
            <Typography variant="body1" sx={{ minWidth: 60 }}>
              Topic
            </Typography>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedTopic}
                onChange={handleTopicChange}
                displayEmpty
              >
                {(topics || []).map((t) => (
                  <MenuItem key={t.id} value={t.topic}>
                    {t.topic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={3} padding={5}>
            {/* LEFT SIDE - VIDEO */}
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" mb={1}>
                  {selectedTopic}
                </Typography>

                <Box
                  sx={{
                    height: 300,
                    border: "1px dashed #ccc",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpenVideo}
                    sx={{
                      backgroundColor: "black",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    }}
                  >
                    Open Video
                  </Button>

                </Box>
              </Card>
            </Grid>

            {/* RIGHT SIDE - PDF */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" mb={2}>
                  Supporting Material
                </Typography>
                <h6>Job aids, PDFs and guided links for the selected topic.</h6>
                <Card>
                  <List>
                    {pdfList.map((pdf, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => handleOpenPdf(pdf)}
                          >
                            Open
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={pdf.name}
                          secondary="Job Aid"
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Card>
            </Grid>
          </Grid>

          {/* VIDEO MODAL */}
          <Dialog
            open={openModal}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
          >
            {/* Header */}
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedTopic}

              <Box>
                {/* Close */}
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            {/* Video */}
            <DialogContent>
              <Box className="video-wrapper">
                {buffering && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#fff",
                      zIndex: 10,
                    }}
                  >
                    <Loader />
                  </Box>
                )}
                <video
                  ref={videoRef}
                  className="video"
                  preload="metadata"
                  playsInline
                  onWaiting={() => setBuffering(true)}
                  onCanPlay={() => setBuffering(false)}
                  onCanPlayThrough={() => setBuffering(false)}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>


                {/* CONTROLS */}
                <Box className="controls">

                  {/* Progress Bar */}
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    value={progress}
                    onChange={handleSeek}
                    className="progress"
                  />

                  <Box className="bottom-controls">

                    <button onClick={backward}>⏪</button>

                    <button onClick={togglePlay}>
                      {isPlaying ? "⏸" : "▶"}
                    </button>

                    <button onClick={forward}>⏩</button>

                    {/* Time */}
                    <span>
                      {formatTime(progress)} / {formatTime(duration)}
                    </span>

                    {/* Volume */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={handleVolume}
                    />

                    <div style={{ flex: 1 }} />

                    {/* Download */}
                    <button onClick={downloadVideo}>⬇</button>

                    {/* Fullscreen */}
                    <button onClick={fullScreen}>⛶</button>

                  </Box>
                </Box>

              </Box>

            </DialogContent>

          </Dialog>

          <Dialog
            open={openPdf}
            onClose={handleClosePdf}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedPdf?.name}

              <Box>
                {/* Download */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePdfDownload}
                  sx={{ mr: 1 }}
                >
                  Download
                </Button>


                {/* Close */}
                <IconButton onClick={handleClosePdf}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              {selectedPdf && (
                <iframe
                  src={selectedPdf?.file}
                  title="PDF Viewer"
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default TrainingContent;
